import { TemplateElement, defineElement, html } from '@webtides/element-js';

export default class CookieConsentCheck extends TemplateElement {
    constructor() {
        super({ shadowRender: true });
        // Keep a stable marker so the banner can be removed from the light DOM
        // after consent and reinserted if consent is revoked later.
        this.bannerPlaceholder = document.createComment('cookie-consent-banner');
        this.bannerElement = null;
        this.domSyncFrame = null;
    }

    properties() {
        return {
            services: [], // array of => category name | service name/id
            handle: 'event', // can be event|iframe
            consented: false,
        };
    }

    watch() {
        return {
            consented: (consented) => {
                if (consented === true) {
                    this.handleConsent();
                }
            },
        };
    }

    connected() {
        const content = this.querySelector('[slot="content"]');
        if (content) content.classList.remove('d-none');
        this.checkConsent();
        this.queueBannerDomSync();
    }

    events() {
        return {
            this: {
                click: async (e) => {
                    if (e.target.hasAttribute('consent-enable')) {
                        if (window.updateCookieConsent) {
                            const consent = {};
                            for (const service of this.services) {
                                consent[service] = true;
                            }
                            await window.updateCookieConsent(consent);
                            this.checkConsent();
                            this.queueBannerDomSync();
                        }
                    }
                },
            },
            document: {
                'cookie-consent-change': () => {
                    this.checkConsent();
                    this.queueBannerDomSync();
                },
            },
        };
    }

    checkConsent() {
        if (window.checkCookieConsent) {
            this.consented = window.checkCookieConsent(this.services);
        }
    }

    handleConsent() {
        if (this.handle === 'event') {
            this.dispatch(
                'cookie-consent-check',
                { services: this.services, consented: this.consented },
                true,
                true,
                true,
            );
        }

        if (this.handle === 'iframe') {
            const iframes = this.querySelectorAll('iframe[data-src]');
            for (const iframe of iframes) {
                iframe.setAttribute('src', iframe.getAttribute('data-src'));
            }
        }
    }

    queueBannerDomSync() {
        if (this.domSyncFrame) {
            cancelAnimationFrame(this.domSyncFrame);
        }

        this.domSyncFrame = requestAnimationFrame(() => {
            this.domSyncFrame = null;
            // Run the DOM mutation after the consent update/render cycle so the
            // slotted banner can be removed reliably from the host element.
            this.syncBannerDom();
        });
    }
    syncBannerDom() {
        if (!this.bannerElement) {
            this.bannerElement = this.querySelector('[slot="banner"]');
        }

        if (!this.bannerElement) return;

        if (this.consented) {
            if (this.bannerElement.parentNode) {
                // Detach the banner entirely so its headline/content is no longer
                // part of the document structure after consent was granted.
                this.bannerElement.replaceWith(this.bannerPlaceholder);
            }
            return;
        }

        if (this.bannerPlaceholder.parentNode) {
            // Restore the original banner node if consent becomes unavailable again.
            this.bannerPlaceholder.replaceWith(this.bannerElement);
        }
    }

    template() {
        return this.consented ? html`<slot name="content"></slot>` : html`<slot name="banner"></slot>`;
    }
}

defineElement('cookie-consent-check', CookieConsentCheck);
